/**
 * RESPONSIVE TABLE COMPONENT
 * Muestra tabla en desktop/tablet y cards en mobile
 */

import * as React from "react";
import { flexRender, type Table as TanStackTable, type ColumnDef } from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import { useIsMobile, useIsTablet } from "@/hooks/use-mobile";

interface ResponsiveTableProps<TData> {
  table: TanStackTable<TData>;
  columns: ColumnDef<TData>[];
  isLoading?: boolean;
  emptyMessage?: string;
  /** Render function for mobile card view */
  renderMobileCard: (row: TData) => React.ReactNode;
  /** Column IDs to hide on tablet (lg breakpoint) */
  hideOnTablet?: string[];
}

export function ResponsiveTable<TData>({
  table,
  columns,
  isLoading,
  emptyMessage = "No hay datos",
  renderMobileCard,
  hideOnTablet = [],
}: ResponsiveTableProps<TData>) {
  const isMobile = useIsMobile();
  const rows = table.getRowModel().rows;

  // Loading spinner component
  const LoadingSpinner = () => (
    <div className="flex items-center justify-center h-24">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
    </div>
  );

  // Mobile: Card view
  if (isMobile) {
    if (isLoading) return <LoadingSpinner />;
    if (!rows?.length) {
      return (
        <div className="flex items-center justify-center h-24 text-muted-foreground">
          {emptyMessage}
        </div>
      );
    }
    return (
      <div className="space-y-3">
        {rows.map((row) => (
          <Card key={row.id} className="overflow-hidden">
            <CardContent className="p-4">{renderMobileCard(row.original)}</CardContent>
          </Card>
        ))}
      </div>
    );
  }

  // Desktop/Tablet: Table view
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  className={hideOnTablet.includes(header.id) ? "hidden lg:table-cell" : ""}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center">
                <LoadingSpinner />
              </TableCell>
            </TableRow>
          ) : rows?.length ? (
            rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    className={hideOnTablet.includes(cell.column.id) ? "hidden lg:table-cell" : ""}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className="h-24 text-center text-muted-foreground"
              >
                {emptyMessage}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

export { useIsMobile, useIsTablet };
